import React, { useState } from "react";
import Box from "@mui/material/Box";
import { Grid, Button } from "@material-ui/core";
import styles from "./customReport.module.css";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker as DatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopTimePicker as TimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import {
    ThemeProvider as MuiV5ThemeProvider,
    createTheme as createMuiV5Theme,
} from "@mui/material/styles";
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
} from "@mui/material";
import { useSelector } from "react-redux";
import useAxiosPrivate from "utils/hooks/useAxiosPrivate";
import moment from "moment";
import Alert from "@material-ui/lab/Alert";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { PRIMARY_THEME_COLOR } from "../../utils/styles/theme.colors";

const pickerTheme = createTheme({
    palette: {
        type: "dark",
        primary: {
            light: PRIMARY_THEME_COLOR,
            main: PRIMARY_THEME_COLOR,
            dark: PRIMARY_THEME_COLOR,
        },
        background: {
            paper: "#272727",
            default: "#000000",
        },
        text: {
            primary: "#ffffff",
            secondary: "#ffffff80",
        },
    },
});

// @mui/x-date-pickers (MUI v5) renders its calendar/clock popup through
// @mui/material/styles theme context, which the v4 MuiThemeProvider above
// is invisible to — without this, the popup falls back to MUI's light defaults.
const pickerPopupTheme = createMuiV5Theme({
    palette: {
        mode: "dark",
        primary: {
            main: PRIMARY_THEME_COLOR,
        },
        background: {
            paper: "#272727",
            default: "#000000",
        },
        text: {
            primary: "#ffffff",
            secondary: "#ffffff80",
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    border: "1px solid #505050",
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    color: "#ffffff",
                    "&.Mui-selected": {
                        color: "#000000",
                    },
                },
            },
        },
    },
});

const pickerTextFieldSlotProps = {
    textField: {
        variant: "outlined",
        size: "small",
        fullWidth: true,
        sx: {
            "& .MuiOutlinedInput-root": {
                color: "#ffffff",
                backgroundColor: "#000000",
                "& fieldset": {
                    borderColor: "var(--primary-theme-color)",
                },
                "&:hover fieldset": {
                    borderColor: "var(--primary-theme-color)",
                },
                "&.Mui-focused fieldset": {
                    borderColor: "var(--primary-theme-color)",
                },
            },
            "& .MuiInputLabel-root": {
                color: "#ffffff80",
            },
            "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--primary-theme-color)",
            },
            "& .MuiFormHelperText-root": {
                color: "#f75f56",
            },
            "& .MuiIconButton-root": {
                color: "#ffffff",
            },
        },
    },
};

const selectSx = {
    color: "#ffffff",
    backgroundColor: "#000000",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--primary-theme-color)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--primary-theme-color)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--primary-theme-color)",
    },
    "& .MuiSvgIcon-root": {
        color: "#ffffff",
    },
};

const selectLabelSx = {
    color: "#ffffff80",
    "&.Mui-focused": {
        color: "var(--primary-theme-color)",
    },
};

const selectMenuProps = {
    PaperProps: {
        sx: {
            backgroundColor: "#272727",
            color: "#ffffff",
            border: "1px solid var(--primary-theme-color)",
            "& .MuiMenuItem-root:hover": {
                backgroundColor: "rgba(var(--primary-theme-color), 0.12)",
            },
            "& .Mui-selected": {
                backgroundColor:
                    "rgba(var(--primary-theme-color), 0.2) !important",
            },
        },
    },
};

export default function CustomReport(props) {
    const { onClose } = props;
    const axios = useAxiosPrivate();

    const users = useSelector((state) => state.user);
    const loggedInUserEmail = useSelector((state) => state.auth.email);
    const selectedUserIds = users.data.map((user) => user.user_id);

    const [alert, setAlert] = useState({
        showAlert: false,
        severity: "error",
        message: "",
    });

    const [initialValues] = useState({
        date: new Date(),
        dateTo: new Date(),
        timeFrom: new Date(),
        timeTo: new Date(),
        type: "csv",
    });

    const reportSchema = Yup.object().shape({
        date: Yup.date().required("Date from is required"),
        dateTo: Yup.date().required("Date to is required"),
        timeFrom: Yup.date().required("Time is required"),
        timeTo: Yup.date().required("Time to is required"),
        type: Yup.string().required("Type is required"),
    });

    const isExportDisabled = (isValid, values) =>
        !isValid ||
        Boolean(values?.type === "pdf" && selectedUserIds?.length > 1);

    const handleSubmit = async (event, actions) => {
        setAlert({
            showAlert: false,
            severity: "",
            message: "",
        });

        const data = {
            date: moment(event.date).format("YYYY-MM-DD"),
            date_to: moment(event.dateTo).format("YYYY-MM-DD"),
            time_from: moment(event.timeFrom).format("HH:mm") + ":00",
            time_to: moment(event.timeTo).format("HH:mm") + ":00",
            email: loggedInUserEmail,
            type: event.type,
            users: selectedUserIds,
        };

        try {
            const post = await axios.post("/report/download", data);

            const url = post?.data?.url;
            if (!url) {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: " No data found",
                });
            } else {
                const link = document.createElement("a");
                link.href = url;
                link.download = "Download";

                link.click();
                actions.resetForm();

                if (onClose) {
                    setTimeout(() => {
                        onClose();
                    }, 1500);
                } else {
                    console.error(
                        "onClose function not provided to CustomReport component",
                    );
                }
            }
        } catch (error) {
            if (error?.response?.data?.message === "user ids are required") {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: "Please choose a patient to download report.",
                });
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: `${error.response?.data?.message}`,
                });
            }
        }
    };

    return (
        <MuiV5ThemeProvider theme={pickerPopupTheme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MuiThemeProvider theme={pickerTheme}>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={reportSchema}
                    >
                        {({
                            isValid,
                            errors,
                            values,
                            touched,
                            setFieldValue,
                        }) => {
                            const exportDisabled = isExportDisabled(
                                isValid,
                                values,
                            );

                            return (
                                <Form>
                                    <Grid container justifyContent="center">
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Grid item xs={6} sm={6} md={6}>
                                                <Box sx={{ height: 70 }}>
                                                    <Field
                                                        component={DatePicker}
                                                        name="date"
                                                        label="Date from"
                                                        value={values.date}
                                                        onChange={(value) =>
                                                            setFieldValue(
                                                                "date",
                                                                value,
                                                                true,
                                                            )
                                                        }
                                                        slotProps={
                                                            pickerTextFieldSlotProps
                                                        }
                                                        error={Boolean(
                                                            touched.date &&
                                                                errors.date,
                                                        )}
                                                        helperText={
                                                            touched.date &&
                                                            errors.date
                                                        }
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Grid item xs={6} sm={6} md={6}>
                                                <Box sx={{ height: 70 }}>
                                                    <Field
                                                        component={DatePicker}
                                                        name="dateTo"
                                                        label="Date to"
                                                        onChange={(value) =>
                                                            setFieldValue(
                                                                "dateTo",
                                                                value,
                                                                true,
                                                            )
                                                        }
                                                        value={values.dateTo}
                                                        slotProps={
                                                            pickerTextFieldSlotProps
                                                        }
                                                        error={Boolean(
                                                            touched.dateTo &&
                                                                errors.dateTo,
                                                        )}
                                                        helperText={
                                                            touched.dateTo &&
                                                            errors.dateTo
                                                        }
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Grid item xs={6} sm={6} md={6}>
                                                <Box sx={{ height: 70 }}>
                                                    <Field
                                                        component={TimePicker}
                                                        name="timeFrom"
                                                        label="Time from"
                                                        onChange={(value) =>
                                                            setFieldValue(
                                                                "timeFrom",
                                                                value,
                                                                true,
                                                            )
                                                        }
                                                        value={values.timeFrom}
                                                        slotProps={
                                                            pickerTextFieldSlotProps
                                                        }
                                                        error={Boolean(
                                                            touched.timeFrom &&
                                                                errors.timeFrom,
                                                        )}
                                                        helperText={
                                                            touched.timeFrom &&
                                                            errors.timeFrom
                                                        }
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Grid item xs={6} sm={6} md={6}>
                                                <Box sx={{ height: 70 }}>
                                                    <Field
                                                        component={TimePicker}
                                                        name="timeTo"
                                                        label="Time to"
                                                        onChange={(value) =>
                                                            setFieldValue(
                                                                "timeTo",
                                                                value,
                                                                true,
                                                            )
                                                        }
                                                        value={values.timeTo}
                                                        slotProps={
                                                            pickerTextFieldSlotProps
                                                        }
                                                        error={Boolean(
                                                            touched.timeTo &&
                                                                errors.timeTo,
                                                        )}
                                                        helperText={
                                                            touched.timeTo &&
                                                            errors.timeTo
                                                        }
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Grid item xs={6} sm={6} md={6}>
                                                <Box sx={{ height: 70 }}>
                                                    <FormControl
                                                        fullWidth
                                                        size="small"
                                                    >
                                                        <InputLabel
                                                            id="type-label"
                                                            sx={selectLabelSx}
                                                        >
                                                            Type
                                                        </InputLabel>
                                                        <Select
                                                            labelId="type-label"
                                                            id="type"
                                                            name="type"
                                                            value={values.type}
                                                            onChange={(e) => {
                                                                setFieldValue(
                                                                    "type",
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            label="Type"
                                                            sx={selectSx}
                                                            MenuProps={
                                                                selectMenuProps
                                                            }
                                                            error={Boolean(
                                                                values?.type ===
                                                                    "pdf" &&
                                                                    selectedUserIds?.length >
                                                                        1,
                                                            )}
                                                        >
                                                            <MenuItem value="csv">
                                                                csv
                                                            </MenuItem>
                                                            <MenuItem value="pdf">
                                                                pdf
                                                            </MenuItem>
                                                        </Select>
                                                        {Boolean(
                                                            values?.type ===
                                                                "pdf" &&
                                                                selectedUserIds?.length >
                                                                    1,
                                                        ) && (
                                                            <FormHelperText
                                                                sx={{
                                                                    color: "#f75f56",
                                                                }}
                                                            >
                                                                Only 1 user
                                                                should be
                                                                selected to
                                                                download the PDF
                                                                report
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            container
                                            spacing={4}
                                            xs={8}
                                            sm={8}
                                            md={8}
                                            style={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                margin: "0rem 0rem 1rem 0rem",
                                            }}
                                        >
                                            <Grid
                                                item
                                                xs={6}
                                                sm={6}
                                                md={6}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Button
                                                    fullWidth
                                                    type="submit"
                                                    disabled={exportDisabled}
                                                    classes={{
                                                        root: exportDisabled
                                                            ? styles.exportButtonDisabled
                                                            : styles.exportButtonEnabled,
                                                    }}
                                                >
                                                    Export {values.type}
                                                </Button>
                                            </Grid>
                                        </Grid>

                                        {alert.showAlert && (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Grid item xs={6} sm={6} md={6}>
                                                    <Box sx={{ height: 70 }}>
                                                        <Alert
                                                            severity={
                                                                alert.severity
                                                            }
                                                            onClose={() =>
                                                                setAlert({
                                                                    ...alert,
                                                                    showAlert: false,
                                                                })
                                                            }
                                                        >
                                                            {alert.message}
                                                        </Alert>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Form>
                            );
                        }}
                    </Formik>
                </MuiThemeProvider>
            </LocalizationProvider>
        </MuiV5ThemeProvider>
    );
}
